import type {
  GlobalSearchRequest,
  GlobalSearchResourceItem,
  GlobalSearchUserResult,
  ISchedule,
  Office
} from "@gettactic/api";
import {
  useAuthenticated,
  useDebouncedEffect,
  useGlobalSearch
} from "@gettactic/hooks";
import React, { createContext, useContext, useState, useMemo } from "react";
import { SEARCH_DEBOUNCE_MS } from "./constants";
import { format } from "date-fns";

interface GlobalSearchData {
  resources: GlobalSearchResourceItem[];
  people: GlobalSearchUserResult[];
  offices: Office[];
  loading: boolean;
  schedulesByUser: Record<string, ISchedule[]>;
  schedulesByResource: Record<string, ISchedule[]>;
  hasNoResults: boolean;
}

const GlobalSearchDataContext = createContext<GlobalSearchData | undefined>(
  undefined
);

export function useGlobalSearchData() {
  const context = useContext(GlobalSearchDataContext);
  if (!context) {
    throw new Error(
      "useGlobalSearchData must be used within GlobalSearchDataProvider"
    );
  }
  return context;
}

interface GlobalSearchDataProviderProps {
  children: React.ReactNode;
  searchQuery?: string;
  isOpen?: boolean;
  startDate?: Date | null;
  endDate?: Date | null;
  selectedOfficeId?: string | null | undefined;
  selectedOfficeIds?: string[];
}

export function GlobalSearchDataProvider({
  children,
  searchQuery = "",
  isOpen = false,
  startDate,
  endDate,
  selectedOfficeId,
  selectedOfficeIds = []
}: GlobalSearchDataProviderProps) {
  const [debouncedQuery, setDebouncedQuery] = useState(searchQuery);

  useDebouncedEffect(() => setDebouncedQuery(searchQuery), SEARCH_DEBOUNCE_MS, [
    searchQuery
  ]);

  // Check if dates are currently provided (not null/undefined)
  // This prevents fetching with fallback values when both dates are null
  const hasDatesProvided =
    (startDate !== null && startDate !== undefined) ||
    (endDate !== null && endDate !== undefined);

  const {
    userContext: { currentOffice, authenticatedUser, deviceTz }
  } = useAuthenticated();

  // Use selectedOfficeIds if available (multiselect), otherwise fall back to selectedOfficeId or currentOffice
  const officeIdsForSearch = React.useMemo(() => {
    if (selectedOfficeIds.length > 0) {
      return selectedOfficeIds;
    }
    const singleOfficeId =
      selectedOfficeId !== undefined
        ? selectedOfficeId
        : currentOffice?.id || null;
    return singleOfficeId ? [singleOfficeId] : [];
  }, [selectedOfficeIds, selectedOfficeId, currentOffice?.id]);

  const dateRange = useMemo(() => {
    const start = startDate || new Date(new Date().setHours(0, 0, 0, 0));
    const end = endDate || new Date(new Date().setHours(23, 59, 59, 999));

    return {
      start_time: start,
      end_time: end
    };
  }, [startDate?.getTime(), endDate?.getTime()]);

  const searchParams = useMemo(() => {
    const params: GlobalSearchRequest = {
      query: debouncedQuery,
      start_time: `${format(dateRange.start_time, "yyyy-MM-dd")}T${format(dateRange.start_time, "HH:mm:ss")}Z`,
      end_time: `${format(dateRange.end_time, "yyyy-MM-dd")}T${format(dateRange.end_time, "HH:mm:ss")}Z`
    };

    if (officeIdsForSearch.length > 0) {
      params.office_ids = officeIdsForSearch;
    }

    return params;
  }, [debouncedQuery, dateRange, officeIdsForSearch]);

  // Only enable the query if at least one date is currently provided
  // This prevents the initial fetch with fallback values when both dates are null
  const shouldEnableQuery = isOpen && hasDatesProvided;

  const { data: globalSearchData, loading: globalSearchLoading } =
    useGlobalSearch(searchParams, { enabled: shouldEnableQuery });

  const value: GlobalSearchData = {
    resources: globalSearchData?.resources || [],
    people: globalSearchData?.users || [],
    offices: authenticatedUser?.offices?.offices || [],
    loading: isOpen && globalSearchLoading,
    schedulesByUser: globalSearchData?.schedulesByUser || {},
    schedulesByResource: globalSearchData?.schedulesByResource || {},
    hasNoResults: globalSearchData?.hasNoResults ?? false
  };

  return (
    <GlobalSearchDataContext.Provider value={value}>
      {children}
    </GlobalSearchDataContext.Provider>
  );
}
